import { Breakpoints } from '@angular/cdk/layout';
import { Component, computed, inject, input, signal } from '@angular/core';
import { useBreakpoint } from '@bynary/composables/observer';
import { SonaSpinnerComponent } from '@sona/angular-ui-components';
import { TargetId } from '@sona/shared-model-common';
import { ScanCategory } from '@sona/shared-scan-model-common';
import { SortingState } from '@tanstack/angular-table';
import * as echarts from 'echarts/core';

import { SonaIssueHistoryCardComponent } from '../../components/cards/issue-history-card.ng';
import { SonaIssueTableCardComponent } from '../../components/cards/issue-table-card.ng';
import { SonaPageHistoryCardComponent } from '../../components/cards/page-history-card.ng';
import { SonaPageTableCardComponent } from '../../components/cards/page-table-card.ng';
import { SonaScoreCardComponent } from '../../components/cards/score-card.ng';
import { SonaScoreHistoryCardComponent } from '../../components/cards/score-history-card.ng';
import { ReportFacade } from '../../services/report-facade.ng';

import type { Metrics } from '@sona/shared-reports-models';

@Component({
    selector: 'sona-seo-report',
    imports: [
        SonaSpinnerComponent,
        SonaIssueTableCardComponent,
        SonaPageTableCardComponent,
        SonaPageHistoryCardComponent,
        SonaScoreHistoryCardComponent,
        SonaIssueHistoryCardComponent,
        SonaScoreCardComponent
    ],
    templateUrl: './seo-report.ng.html',
    styles: `
        @media (width < 40rem) {
            sona-card {
                --bs-card-border-radius: 0;
            }

            .tw-container {
                padding-inline: 0;
            }
        }
    `,
    host: {
        class: 'tw-flex tw-flex-col tw-gap-16',
        id: 'brn-tabs-content-seo'
    }
})
export class SonaSeoReportComponent {

    protected readonly historyChartGroup = 'history-charts';

    protected readonly facade = inject(ReportFacade);
    private readonly isMobile = useBreakpoint(Breakpoints.Handset);

    public readonly id = input.required<TargetId>();

    protected readonly category = signal(ScanCategory.SEO);
    protected readonly chartLength = computed(() => this.isMobile() ? 7 : 10);
    protected readonly issuesSorting = signal<SortingState>([ { id: 'issueCount', desc: true } ]);
    protected readonly pagesSorting = signal<SortingState>([{ id: 'score', desc: false }]);

    protected readonly issuesResource = this.facade.getIssues(() => ({
        pagination: { pageIndex: 0, pageSize: 5 },
        sorting: this.issuesSorting()
    }));
    protected readonly pagesResource = this.facade.getPages(() => ({
        pagination: { pageIndex: 0, pageSize: 5 },
        sorting: this.pagesSorting()
    }));

    protected readonly target = computed(() => this.facade.latestCategoryReport.value()?.target);
    protected readonly metrics = computed<Metrics[] | undefined>(() => this.facade.latestCategoryReport.value()?.metrics);
    protected readonly history = computed(() => this.facade.latestCategoryReport.value()?.history);
    protected readonly totalScore = computed(() => this.facade.latestCategoryReport.value()?.score);
    protected readonly totalPages = computed(() => this.facade.latestCategoryReport.value()?.pages);
    protected readonly issues = computed(() => this.facade.latestCategoryReport.value()?.issues);

    constructor() {
        echarts.connect(this.historyChartGroup);
    }
}
